import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BrandXXS } from '@sorare/core/src/atoms/typography';
import { Ordinal } from '@sorare/core/src/atoms/typography/Ordinal';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { glossary } from '@sorare/core/src/lib/glossary';

import type { ContenderLiveFooter_so5Ranking } from './__generated__/index.graphql.ts';

type Props = {
  so5Ranking?: ContenderLiveFooter_so5Ranking;
};

export const ContenderLiveProjectedRanking = ({ so5Ranking }: Props) => {
  // Hide projected ranking range in US Sports since it's not working
  const { sport } = useSportContext();

  if (!so5Ranking) {
    return null;
  }

  const { liveProjectedRanking, liveWorstRanking, liveBestRanking } =
    so5Ranking;

  if (!liveProjectedRanking && !liveBestRanking && !liveWorstRanking) {
    return null;
  }

  return (
    <Horizontal gap={0.5} center>
      {!!liveProjectedRanking && (
        <BrandXXS color="var(--c-nd-600)">
          <FormattedMessage
            id="ContenderLiveProjectedRanking.proj"
            defaultMessage="Proj:"
          />{' '}
          <Ordinal i18n={glossary.ordinal} ordinal={liveProjectedRanking} />
        </BrandXXS>
      )}

      {sport === Sport.FOOTBALL && !!liveBestRanking && !!liveWorstRanking && (
        <BrandXXS color="var(--c-nd-400)">
          (<Ordinal i18n={glossary.ordinal} ordinal={liveBestRanking} />
          {' - '}
          <Ordinal i18n={glossary.ordinal} ordinal={liveWorstRanking} />)
        </BrandXXS>
      )}
    </Horizontal>
  );
};

ContenderLiveProjectedRanking.fragments = {
  so5Ranking: gql`
    fragment ContenderLiveFooter_so5Ranking on So5Ranking {
      id
      liveProjectedRanking
      liveWorstRanking
      liveBestRanking
    }
  ` as TypedDocumentNode<ContenderLiveFooter_so5Ranking>,
};
